import React, { useState, useEffect, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import { Translation, getI18n } from 'react-i18next';
import {
  Affix,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Table,
  Space,
  Row,
  Spin,
} from 'antd';
import {
  LoadingOutlined,
  PlusOutlined,
  EditOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import TimeInput from '../../calendar/components/TimeInput';
import BodyInput from './BodyInput';
import RecurringInput from './RecurringInput';
import CareGuidesPartnership from '../../careGuideHelpers/containers/CareGuidesPartnershipContainer';
import ParticipantInputRow from './ParticipantInputRow';
import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import { pathTo } from '../../../Routes';
import Format from '../../../../../lib/Format';
import Logger from '../../../../../lib/Logger';
import Config from '../../../../../Config';
import EventParticipantFormModal from '../containers/EventParticipantFormModalContainer';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';
import TimezoneInput from '../../timezones/containers/TimezoneInputContainer';

const CalendarEventAdminForm = ({
  id,
  tz,
  data,
  errors,
  load,
  loadMembersPartnership,
  destroyForm,
  isLoading,
  isSubmitting,
  created_id,
  pageParticipants,
  loadParticipants,
  areParticipantEventsLoading,
  list,
  total,
  setPage,
  deleteParticipant,
  showParticipantForm,
  trackerActions,
  ...props
}) => {
  const [redirectTo, setRedirectTo] = useState(null);
  const [careGuideIds, setCareGuideIds] = useState(data['care_guides']);
  const [isChangeCareGuide, setIsChangeCareGuide] = useState(false);
  const [isMultipleCareGuide, setIsMultipleCareGuide] = useState(false);
  const [isAllDay, setIsAllDay] = useState(false);
  const [isMembersIndividual, setIsMembersIndividual] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [startDate, setStartDate] = useState(null);

  const [form] = Form.useForm();

  const apiDatetimeFormat = Config.get('API_DATETIME_FORMAT');
  // form column settings
  const layout = {
    main: {
      labelCol: { span: 5 },
      wrapperCol: { span: 19 },
    },
  };

  const formItemCheckBox = {
    labelCol: {
      sm: {
        span: 8,
      },
    },
    wrapperCol: {
      sm: {
        span: 10,
        offset: 5,
      },
    },
  };

  const pagination = id
    ? {
        onChange: (newPage) => {
          setPage(newPage);
        },
        position: ['topLeft'],
        current: pageParticipants,
        total: total,
        showSizeChanger: false,
      }
    : null;

  const onChangeIsMultipleCareGuide = (evt) => {
    form.setFieldsValue({
      care_guides: [],
      participants: [],
    });
    setIsChangeCareGuide(true);
    setIsMultipleCareGuide(evt.target.checked);
  };

  const onChangeIsMembersIndividual = (evt) => {
    if (!id) {
      form.setFieldsValue({
        participants: [],
      });
    }
    setIsMembersIndividual(evt.target.checked);
  };

  // load record data from API
  useEffect(() => {
    if (id) {
      loadParticipants(id, pageParticipants);
    }
  }, [id, loadParticipants, pageParticipants]);

  useEffect(() => {
    if (id) {
      load(props.activeId, id);
    }
  }, [props.activeId, id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);

  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);

      dataObj['start_at_date'] = dataObj['start_at_date']
        ? moment(dataObj['start_at_date'], apiDatetimeFormat)
        : null;

      dataObj['recurring_end_at'] = dataObj['recurring_end_at']
        ? moment(dataObj['recurring_end_at'], apiDatetimeFormat)
        : null;
      setIsAllDay(dataObj['is_all_day']);
      setIsRecurring([2, 3, 4, 5].includes(dataObj['recurring']));
      setCareGuideIds(dataObj['care_guides']);
      setStartDate(dataObj['start_at_date']);
      onRecurringChangeHandler(data['recurring']);
      if (dataObj['care_guides'].length > 0) {
        setIsMultipleCareGuide(true);
      }
      setIsMembersIndividual(true);
      setPage(1);
      loadParticipants(id, pageParticipants);
      form.setFieldsValue(dataObj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dataString, isSubmitting, id, apiDatetimeFormat]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setRedirectTo(
        pathTo('CalendarEventAdminEditScreen', {
          id_partnership: props.activeId,
          id: created_id,
        })
      );
    }
    return () => {
      destroyForm();
    };
  }, [created_id, props.activeId, setRedirectTo, destroyForm]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `CalendarEventAdminForm.submitData(###)`);

    // API POST/PUT payload

    let payload = {};
    for (const input of Object.keys(data)) {
      if (input in values) {
        // transform entity refs to IDs
        if (['care_guides'].includes(input)) {
          payload['care_guide_ids'] = values[input];
        } else if (['parent_event'].includes(input)) {
          if (values[input]) {
            payload[input + '_id'] = values[input];
          }
        }
        // transform boolean values
        else if (['is_all_day'].includes(input)) {
          const formTimezone = values['timezone'] || tz;

          payload[input] = values[input] ? true : false;

          if (values[input]) {
            payload['start_at'] = moment
              .tz(
                values['start_at_date'].format('YYYY-MM-DD'),
                'YYYY-MM-DD',
                formTimezone
              )
              .startOf('day')
              .utc()
              .format(apiDatetimeFormat);

            payload['recurring_end_at'] = values['recurring_end_at']
              ? moment
                  .tz(
                    values['recurring_end_at'].format('YYYY-MM-DD'),
                    'YYYY-MM-DD',
                    formTimezone
                  )
                  .endOf('day')
                  .utc()
                  .format(apiDatetimeFormat)
              : null;
          } else {
            // prep moment date objects so we can manipulate them
            const endDateObj = values['end_at_date']
              ? moment(values['end_at_date'], 'MM/DD/YYYY')
              : null;

            const endTimeObj = values['end_at_time']
              ? moment(values['end_at_time'], ['h:mm A'])
              : null;
            const startTimeObj = moment(values['start_at_time'], ['h:mm A']);

            // if end time is less than start time then increment end date by one day
            if (endTimeObj && endTimeObj.isBefore(startTimeObj)) {
              endDateObj.add(1, 'day');
            }

            const formTimezone = values['timezone'] || tz;

            const startDate2 = moment
              .tz(
                values['start_at_date'].format('YYYY-MM-DD') +
                  ' ' +
                  startTimeObj.format('HH:mm'),
                'YYYY-MM-DD HH:mm',
                formTimezone
              )
              .utc();

            const endDate =
              endDateObj && endTimeObj
                ? moment
                    .tz(
                      endDateObj.format('YYYY-MM-DD') +
                        ' ' +
                        endTimeObj.format('HH:mm'),
                      'YYYY-MM-DD HH:mm',
                      formTimezone
                    )
                    .utc()
                : null;

            const recurringEndDate =
              values['recurring_end_at'] !== null
                ? moment
                    .tz(
                      values['recurring_end_at'].format('YYYY-MM-DD') +
                        ' ' +
                        endTimeObj.format('HH:mm'),
                      'YYYY-MM-DD HH:mm',
                      formTimezone
                    )
                    .utc()
                : null;

            payload['start_at'] = startDate2.format(apiDatetimeFormat);
            payload['end_at'] = endDate
              ? endDate.format(apiDatetimeFormat)
              : null;
            payload['recurring_end_at'] = recurringEndDate
              ? recurringEndDate.format(apiDatetimeFormat)
              : null;
          }
        } else if (
          [
            'start_at_date',
            'start_at_time',
            'end_at_date',
            'end_at_time',
            'recurring_end_at',
          ].includes(input)
        ) {
          // do nothing
        }
        // handle participants
        else if (input === 'participants') {
          if (Array.isArray(values[input])) {
            payload[input] = values[input].map((x, i) => {
              const { id, key, profile, ...participant } = x; // remove unused properties
              return { user_profile_id: profile.id, ...participant };
            });
          } else {
            payload[input] = [];
          }
        } else if (['url_event_partner', 'url_label'].includes(input)) {
          if (values[input]) {
            if (values[input].length > 0) {
              payload[input] = values[input];
            }
          }
        } else {
          payload[input] = values[input];
        }
      } else if (input === 'participants') {
        if (!isMembersIndividual) {
          payload['is_members_individual'] = isMembersIndividual;
        }
      }
    }
    payload['partnership_id'] = props.activeId;

    if (id) {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.CALENDAR_EVENTS_ADMIN_EDIT_SUBMIT
      );

      // update
      props.update(id, payload, (success) => {
        if (success) {
          setIsChangeCareGuide(false);
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.CALENDAR_EVENTS_ADMIN_ADD_SUBMIT
      );

      // create
      props.create(payload, (success) => {
        if (success) {
          setIsChangeCareGuide(false);
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `CalendarEventAdminForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `CalendarEventAdminForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  const onStartTimeChangeHandler = (value, option) => {
    // get end time value (as string)
    const endTimeValue = form.getFieldValue('end_at_time');

    // get date values (as moment.js objects)
    const startDate2 = form.getFieldValue('start_at_date');
    const endDate = form.getFieldValue('end_at_date');

    // turn time values into moment.js objects
    const endTime = endTimeValue ? moment(endTimeValue, ['h:mm A']) : null;
    const startTime = moment(value, ['h:mm A']);

    // if `end_at_time` has no value, set time to `start_at_time` (value) + 1 hour
    if (!endTimeValue) {
      form.setFieldsValue({
        end_at_time: startTime.add(1, 'hour').format('h:mm A'),
      });

      // if `start_at_date` and `end_at_date` are set to same day and `start_at_time` is later than `end_at_time`,
      // set time to `start_at_time` (value) + 1 hour
      // } else if (startDate2 && endDate && startDate2.format("YYYY-MM-DD") === endDate.format("YYYY-MM-DD") && startTime && endTime && startTime > endTime) {
    } else if (
      startDate2 &&
      endDate &&
      startDate2.format('MM/DD/YYYY') === endDate &&
      startTime &&
      endTime &&
      startTime >= endTime
    ) {
      form.setFieldsValue({
        end_at_time: startTime.add(1, 'hour').format('h:mm A'),
      });
    }
  };

  const onEndTimeChangeHandler = (value, option) => {
    // get start time value (as string)
    const startTimeValue = form.getFieldValue('start_at_time');

    // get date values (as moment.js objects)
    const startDate2 = form.getFieldValue('start_at_date');
    const endDate = form.getFieldValue('end_at_date');

    // turn time values into moment.js objects
    const startTime = startTimeValue
      ? moment(startTimeValue, ['h:mm A'])
      : null;
    const endTime = moment(value, ['h:mm A']);

    // if `start_at_date` and `end_at_date` are set to same day and `start_at_time` is later than `end_at_time`,
    // set time to `start_at_time` (value) + 1 hour
    // if (startDate2 && endDate && startDate2.format("YYYY-MM-DD") === endDate.format("YYYY-MM-DD") && startTime && endTime && startTime > endTime) {
    if (
      startDate2 &&
      endDate &&
      startDate2.format('MM/DD/YYYY') === endDate &&
      startTime &&
      endTime &&
      startTime > endTime
    ) {
      form.setFieldsValue({
        end_at_time: startTime.add(1, 'hour').format('h:mm A'),
      });
    }
  };

  const onChangeStartDate = (date, dateString) => {
    setStartDate(date);
    form.setFieldsValue({ end_at_date: Format.date(date, 'MM/DD/YYYY') });
    const lastDate = form.getFieldValue('recurring_end_at');
    const recurring = form.getFieldValue('recurring');
    if ([2, 3, 4, 5].includes(recurring) && (!lastDate || lastDate < date)) {
      form.setFieldsValue({ recurring_end_at: date });
    }
  };

  const disableLastDateHandler = (current) => {
    return startDate && current && current < startDate.startOf('day');
  };

  const onRecurringChangeHandler = (val) => {
    setIsRecurring([2, 3, 4, 5].includes(val));
    form.setFieldsValue({ recurring_end_at: null });
  };

  const handleChange = (value) => {
    if (form) {
      form.setFieldsValue({
        participants: [],
      });
    }
    setCareGuideIds(value);
    return value;
  };

  const participantColumns = [
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
    {
      title: 'Actions',
      dataIndex: 'edit',
      key: 'edit',
      width: 100,
    },
  ];

  const removeTracked = useCallback(
    (partnershipId, id, cb) => {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.CALENDAR_EVENTS_ADMIN_DELETE
      );
      return props.delete(partnershipId, id, cb);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.delete, trackerActions]
  );

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <Translation>
      {(t) => (
        <>
          <div className='calendar-event-form'>
            <Form
              name='calendar_event_form'
              form={form}
              initialValues={data}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              validateTrigger='onSubmit'
              {...layout.main}
            >
              <Row gutter={16}>
                <Col xs={24} lg={18}>
                  <Card
                    title={
                      id
                        ? t('calendar_event_edit_title')
                        : t('calendar_event_add_title')
                    }
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <div className='form-group'>
                      <Form.Item
                        name='title'
                        label={t('calendar_event_title')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                          {
                            type: 'string',
                            min: 0,
                            max: 250,
                            message: t('feedback_validation_char_range', {
                              min: 0,
                              max: 250,
                            }),
                          },
                        ]}
                      >
                        <Input disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='is_all_day'
                        valuePropName='checked'
                        {...formItemCheckBox}
                      >
                        <Checkbox
                          onChange={(evt) => setIsAllDay(evt.target.checked)}
                          disabled={isLoading || isSubmitting}
                        >
                          {t('calendar_event_is_all_day')}
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='start_at_date'
                        style={{ marginBottom: 0 }}
                        label={t('calendar_event_start_date')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Row gutter={16}>
                          <Col span={8}>
                            <div className='form-group'>
                              <Form.Item name='start_at_date'>
                                <DatePicker
                                  placeholder={t('calendar_event_start_date')}
                                  format='MM/DD/YYYY'
                                  onChange={onChangeStartDate}
                                  disabled={isLoading || isSubmitting}
                                  inputReadOnly
                                />
                              </Form.Item>
                            </div>
                          </Col>

                          <Col span={8}>
                            {!isAllDay ? (
                              <TimeInput
                                name='start_at_time'
                                placeholder={t(
                                  'calendar_event_form_input_start_at_time'
                                )}
                                label={t(
                                  'calendar_event_form_input_start_at_time'
                                )}
                                rules={[
                                  {
                                    required: true,
                                    message: t('feedback_validation_required'),
                                  },
                                ]}
                                disabled={isLoading || isSubmitting}
                                onChange={onStartTimeChangeHandler}
                              />
                            ) : null}
                          </Col>

                          {!isAllDay ? (
                            <Col span={8}>
                              <TimeInput
                                placeholder={t(
                                  'calendar_event_form_input_end_at_time'
                                )}
                                name='end_at_time'
                                label={t(
                                  'calendar_event_form_input_end_at_time'
                                )}
                                rules={[
                                  {
                                    required: true,
                                    message: t('feedback_validation_required'),
                                  },
                                ]}
                                disabled={isLoading || isSubmitting}
                                onChange={onEndTimeChangeHandler}
                              />
                            </Col>
                          ) : null}

                          <Form.Item name='end_at_date'>
                            <Input type='hidden' />
                          </Form.Item>
                        </Row>
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='recurring'
                        style={{ marginBottom: 0 }}
                        label={t('calendar_event_recurring')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Row gutter={16}>
                          <Col span={8}>
                            <RecurringInput
                              name='recurring'
                              disabled={isLoading || isSubmitting}
                              onChange={onRecurringChangeHandler}
                            />
                          </Col>

                          <Col span={8}>
                            <div className='form-group'>
                              <Form.Item
                                name='recurring_end_at'
                                label={t('calendar_event_last_date')}
                              >
                                <DatePicker
                                  inputReadOnly
                                  placeholder={
                                    isRecurring
                                      ? t(
                                          'calendar_event_form_placeholder_recurring_end_at_date'
                                        )
                                      : null
                                  }
                                  format='MM/DD/YYYY'
                                  allowClear={true}
                                  disabledDate={disableLastDateHandler}
                                  disabled={
                                    !isRecurring || isLoading || isSubmitting
                                  }
                                />
                              </Form.Item>
                            </div>
                          </Col>
                        </Row>
                      </Form.Item>
                    </div>
                    <TimezoneInput
                      name='timezone'
                      label={t('user_account_form_input_timezones')}
                      rules={[
                        {
                          required: true,
                          message: t('feedback_validation_required'),
                        },
                      ]}
                      disabled={isLoading || isSubmitting}
                    />
                    <BodyInput
                      name='description'
                      label={t('calendar_event_description')}
                      form={form}
                      defaultValue={form.getFieldValue(['description'])}
                      disabled={isLoading || isSubmitting}
                    />

                    <div className='form-group'>
                      <Form.Item
                        name='url_event_partner'
                        label={t('calendar_event_url_event_partner')}
                      >
                        <Input autoFocus disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>
                    <div className='form-group'>
                      <Form.Item
                        name='url_label'
                        label={t('calendar_event_url_label')}
                        rules={[
                          {
                            type: 'string',
                            min: 1,
                            max: 20,
                            message: t('feedback_validation_length', {
                              min: 1,
                              max: 20,
                            }),
                          },
                        ]}
                      >
                        <Input autoFocus disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        {...formItemCheckBox}
                        name='is_multiple_care_guide'
                      >
                        <Checkbox
                          checked={isMultipleCareGuide}
                          onChange={(evt) => onChangeIsMultipleCareGuide(evt)}
                          disabled={isLoading || isSubmitting}
                        >
                          {t('calendar_event_is_multiple_care_guide')}
                        </Checkbox>
                      </Form.Item>
                    </div>

                    {isMultipleCareGuide && (
                      <CareGuidesPartnership
                        form={form}
                        multiple={true}
                        setCareGuideIds={setCareGuideIds}
                        name='care_guides'
                        label={t('calendar_event_care_guide_id')}
                        partnershipId={props.activeId}
                        handleChange={handleChange}
                        isSubmitting={isSubmitting}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      />
                    )}

                    <div className='form-group'>
                      <Form.Item
                        {...formItemCheckBox}
                        name='is_members_individual'
                      >
                        <Checkbox
                          checked={isMembersIndividual}
                          onChange={(evt) => onChangeIsMembersIndividual(evt)}
                          disabled={isLoading || isSubmitting}
                        >
                          {t('calendar_event_is_participants_individually')}
                        </Checkbox>
                      </Form.Item>
                    </div>

                    {isMembersIndividual && (
                      <>
                        {id ? (
                          <Form.Item label={t('calendar_event_participants')}>
                            <div className='calendar-participants'>
                              <Table
                                className='calendar-event-table-participants'
                                pagination={
                                  pageParticipants === 1 && total <= 10
                                    ? false
                                    : !isChangeCareGuide
                                    ? pagination
                                    : false
                                }
                                columns={participantColumns}
                                loading={areParticipantEventsLoading}
                                dataSource={
                                  !isChangeCareGuide
                                    ? list
                                      ? list.map((x) => {
                                          return {
                                            key: x.id,
                                            value: (
                                              <>
                                                {x.profile?.first_name}{' '}
                                                {x.profile?.last_name}
                                              </>
                                            ),
                                            status: t(
                                              'calendar_event_detail_public_status_' +
                                                x.public_status
                                            ),
                                            edit: (
                                              <>
                                                <Space>
                                                  <Button
                                                    icon={<EditOutlined />}
                                                    onClick={() =>
                                                      showParticipantForm(x.id)
                                                    }
                                                  />
                                                  <Button
                                                    onClick={() =>
                                                      deleteParticipant(
                                                        x.id,
                                                        (success) => {
                                                          if (success) {
                                                            loadParticipants(
                                                              id,
                                                              pageParticipants
                                                            );
                                                            message.success(
                                                              getI18n().t(
                                                                'feedback_delete_success'
                                                              )
                                                            );
                                                          } else {
                                                            message.error(
                                                              getI18n().t(
                                                                'feedback_delete_error'
                                                              )
                                                            );
                                                          }
                                                        }
                                                      )
                                                    }
                                                    icon={
                                                      <MinusCircleOutlined />
                                                    }
                                                  />
                                                </Space>
                                              </>
                                            ),
                                          };
                                        })
                                      : []
                                    : []
                                }
                                size='small'
                                showHeader={false}
                              />
                            </div>
                            <Form.Item style={{ marginBottom: 0 }}>
                              <Button
                                type='dashed'
                                onClick={() => {
                                  trackerActions(
                                    mixpanelEventsEnum.ACTIONS_PARTNER
                                      .CALENDAR_EVENTS_ADMIN_SHOW_PARTICIPANT_FORM_MODAL
                                  );
                                  showParticipantForm(null);
                                }}
                                block
                              >
                                <PlusOutlined />
                                {t('calendar_event_participant_button_add')}
                              </Button>
                            </Form.Item>
                          </Form.Item>
                        ) : (
                          <Form.Item label={t('calendar_event_participants')}>
                            <Form.List name='participants'>
                              {(participants, { add, remove, move }) => {
                                return (
                                  <>
                                    <div className='scroll-participants'>
                                      {participants.map((participant, i) => (
                                        <ParticipantInputRow
                                          form={form}
                                          isMultipleCareGuide={
                                            isMultipleCareGuide
                                          }
                                          partnershipId={props.activeId}
                                          participant={participant}
                                          careGuideIds={careGuideIds}
                                          disabled={isLoading || isSubmitting}
                                          remove={remove}
                                          index={i}
                                          key={i}
                                        />
                                      ))}
                                    </div>

                                    <Form.Item style={{ marginBottom: 0 }}>
                                      <Button
                                        type='dashed'
                                        onClick={() => {
                                          trackerActions(
                                            mixpanelEventsEnum.ACTIONS_PARTNER
                                              .CALENDAR_EVENTS_ADMIN_ADD_PARTICIPANT
                                          );
                                          add();
                                        }}
                                        block
                                      >
                                        <PlusOutlined />
                                        {t(
                                          'calendar_event_participant_button_add'
                                        )}
                                      </Button>
                                    </Form.Item>
                                  </>
                                );
                              }}
                            </Form.List>
                          </Form.Item>
                        )}
                      </>
                    )}
                  </Card>
                </Col>

                <Col xs={24} lg={6}>
                  <Affix offsetTop={10}>
                    <Card title={t('form_metadata_header')}>
                      <FormMetadata
                        id={id}
                        activeDelete={true}
                        partnershipId={props.activeId}
                        isSubmitting={isSubmitting}
                        delete={removeTracked}
                        deleteRedirectTo='CalendarEventsAdminScreen'
                        createdAt={props.createdAt}
                        updatedAt={props.updatedAt}
                      />
                    </Card>
                  </Affix>
                </Col>
              </Row>
            </Form>
            <EventParticipantFormModal
              options={[]}
              eventId={id}
              isMultipleCareGuide={isMultipleCareGuide}
              partnershipId={props.activeId}
              careGuideIds={careGuideIds}
              trackerActions={trackerActions}
            />
          </div>
        </>
      )}
    </Translation>
  );
};

export default CalendarEventAdminForm;

Logger.log('silly', `CalendarEventAdminForm loaded.`);
